import { getIntl } from './intl';
import * as luxon from 'luxon';
import { validateISODateFormat } from './validateDateFormat';
import { TempAndPersistedAvatarState } from '../components/v2BaseComponents/ImagePicker/TempImagePicker';

const intl = getIntl('validators', {
    requiredMessage: 'This field is required',
    invalidZipCode: 'Invalid zip code',
    invalidDateFormat: 'Date must be in the format: ',
    invalidEmail: 'Invalid email address',
    minAge: 'Must be older than ',
    consentCheck: 'Consent must be accepted',
    acceptanceCheck: 'Must be accepted',
    phoneNumber: 'Invalid phone number. Must be in the format (###) ###-####',
    faxNumber: 'Invalid fax number. Must be in the format (###) ###-####',
    minValue: 'Must be at least {min}',
    minValueExclusive: 'Must be greater than {min}',
    maxValue: 'Must be at most {max}',
    maxValueExclusive: 'Must be less than {max}',
    futureDate: 'Must be in the future',
    mustComeAfter: 'Must come after the start date',
    mustComeBefore: 'Must be before the end date',
    invalidColorHex: 'Must be valid color hex (#FFFFFF)',
});

export type Validator = (value: unknown) => string | undefined;

export const required: Validator = (value: unknown): string | undefined => {
    const message = intl('requiredMessage');

    if (Array.isArray(value)) {
        return value.length > 0 ? undefined : message;
    } else if (typeof value === 'string') {
        return value.trim() ? undefined : message;
    } else {
        return value === null || value === undefined ? message : undefined;
    }
};

export const getRequiredTempAvatar =
    (initialImageUrl: string | null | undefined): Validator =>
    (value: unknown): string | undefined => {
        if (!value && !initialImageUrl) return intl('requiredMessage');

        if (!value && !!initialImageUrl) return;

        const casted = value as TempAndPersistedAvatarState;
        if (casted.type === 'updated') {
            return undefined;
        } else if (casted.type === 'not-modified' && initialImageUrl) {
            return undefined;
        } else {
            return intl('requiredMessage');
        }
    };

// @ts-expect-error TS2322
export const zipCode: Validator = (value: string): string | undefined => {
    const message = intl('invalidZipCode');

    if (!value) return undefined;

    if (value.length === 5 && /^\d+$/.test(value)) {
        return undefined;
    } else {
        return message;
    }
};

export const getRawDateFormatValidator = (validationFormat: string, displayFormat: string): Validator => {
    return (value: unknown): string | undefined => {
        if (!value) return undefined;

        if (typeof value !== 'string') return;

        if (!luxon.DateTime.fromFormat(value, validationFormat).isValid) {
            return intl('invalidDateFormat') + displayFormat;
        }
    };
};

export const getDateFormatValidator =
    (displayFormat: string): Validator =>
    // @ts-expect-error TS2322
    (value: string): string | undefined => {
        if (!value) return undefined;

        if (!validateISODateFormat(value)) {
            return intl('invalidDateFormat') + displayFormat;
        }
    };

export const getMustComeAfterValidator = (date: string | null): Validator => {
    return (value: unknown): string | undefined => {
        if (!date || !value || typeof value !== 'string') return;

        const isDateValid = (value: string) => {
            if (!value) return false;

            return luxon.DateTime.fromISO(value).isValid;
        };

        if (!isDateValid(value) || !isDateValid(date)) return;

        if (luxon.DateTime.fromISO(value).toJSDate().getTime() <= luxon.DateTime.fromISO(date).toJSDate().getTime()) {
            return intl('mustComeAfter');
        }
    };
};

export const mustComeBeforeValidator = (date: string | null): Validator => {
    return (value: unknown): string | undefined => {
        if (!date || !value || typeof value !== 'string') return;

        const isDateValid = (value: string) => {
            if (!value) return false;

            return luxon.DateTime.fromISO(value).isValid;
        };

        if (!isDateValid(value) || !isDateValid(date)) return;

        if (luxon.DateTime.fromISO(value).toJSDate().getTime() >= luxon.DateTime.fromISO(date).toJSDate().getTime()) {
            return intl('mustComeBefore');
        }
    };
};

// @ts-expect-error TS2322
export const futureDate: Validator = (value: string): string | undefined => {
    if (!value) return undefined;

    if (!validateISODateFormat(value)) {
        return intl('invalidDateFormat');
    }

    if (luxon.DateTime.fromISO(value).toJSDate().getTime() < new Date().getTime()) {
        return intl('futureDate');
    }
};

export const minAge =
    (minAge: number): Validator =>
    // @ts-expect-error TS2322
    (value: string): string | undefined => {
        if (!value) return undefined;

        const errorMessage = intl('minAge') + minAge;
        if (!validateISODateFormat(value)) {
            return errorMessage;
        }

        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - minAge);

        if (luxon.DateTime.fromISO(value).toJSDate().getTime() > minDate.getTime()) {
            return errorMessage;
        }
    };

// @ts-expect-error TS2322
export const email: Validator = (value: string): string | undefined => {
    if (!value) return undefined;

    // Regular expression for email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return emailRegex.test(value) ? undefined : intl('invalidEmail');
};

// @ts-expect-error TS2322
export const consentCheck: Validator = (value: boolean): string | undefined => {
    if (!value) return intl('consentCheck');
};

export const acceptanceCheck: Validator = (value: unknown): string | undefined => {
    if (typeof value !== 'boolean') return intl('acceptanceCheck');

    if (!value) return intl('acceptanceCheck');
};

export const composeValidators =
    (...validators: ((value: unknown) => string | undefined)[]): Validator =>
    (value: unknown) =>
        validators.reduce((error, validator) => error || validator(value), undefined as string | undefined);

export const valuesMustMatch = (otherValue: string | number, errorMessage: string) => (value: unknown) => {
    if (typeof value !== 'string' && typeof value !== 'number') return;

    if (otherValue !== value) return errorMessage;
};

/**
 * Validates a phone number in the format (###) ###-####
 *
 * This format is what was requested and matches react-native-mask-input
 * https://github.com/CaioQuirinoMedeiros/react-native-mask-input/blob/main/src/Masks.ts
 */
export const phoneNumberReg = /^\([2-9]\d\d\) [2-9]\d\d-\d\d\d\d$/;
export const isPhoneNumberValid = (value: string): boolean => phoneNumberReg.test(value);
const phoneFaxImpl =
    (type: 'phone' | 'fax') =>
    (value: unknown): string | undefined => {
        if (typeof value !== 'string') return;

        if (!value) return;

        // support backwards compatibility before inputs were masked with react-native-mask-input or validated
        if (!value.startsWith('(')) return;

        if (!phoneNumberReg.test(value)) {
            return intl(`${type}Number`);
        }
    };
export const phoneNumber = phoneFaxImpl('phone');
export const faxNumber = phoneFaxImpl('fax');
export const numeric = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return;

    if (!value) return;

    if (isNaN(Number(value))) {
        return 'Must be a number';
    }
};

export const integer = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return;

    if (!value) return;

    if (!Number.isInteger(Number(value))) {
        return 'Must be a whole number';
    }
};

export const minValue =
    (min: number) =>
    (value: unknown): string | undefined => {
        if (typeof value !== 'string') return;

        if (!value) return;

        if (Number(value) < min) {
            return intl('minValue', { min });
        }
    };

export const minValueExclusive =
    (min: number) =>
    (value: unknown): string | undefined => {
        if (typeof value !== 'string') return;

        if (!value) return;

        if (Number(value) <= min) {
            return intl('minValueExclusive', { min });
        }
    };

export const maxValue =
    (max: number) =>
    (value: unknown): string | undefined => {
        if (typeof value !== 'string') return;

        if (!value) return;

        if (Number(value) > max) {
            return intl('maxValue', { max });
        }
    };

export const maxValueExclusive =
    (max: number) =>
    (value: unknown): string | undefined => {
        if (typeof value !== 'string') return;

        if (!value) return;

        if (Number(value) >= max) {
            return intl('maxValueExclusive', { max });
        }
    };

export const colorHex = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return;

    if (!value) return;

    if (!/^[A-Fa-f0-9]{6}$/i.test(value)) {
        return intl('invalidColorHex');
    }
};
